import { useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useMeCore } from '../../../core/modules/me';
import { aiChatService, AiChatHistoryItem } from '../../../core/modules/aiChat/services/api';
import AiChatBubble from './AiChatBubble';
import AiChatPanel from './AiChatPanel';
import { AiChatMessageData } from './AiChatMessage';

// Rutas de Layout.tsx que traen un :id de empresa en la URL (todas montadas
// bajo /dashboard/*, ver App.tsx) -- si el usuario esta parado en una de
// estas, resolvemos la empresa del chat desde ahi antes que nada. Mantener
// en sync con las rutas de Layout.tsx si se agregan pantallas nuevas con :id.
const COMPANY_ID_ROUTE_PATTERNS = [
  '/dashboard/metrics/:id',
  '/dashboard/metric-details/:id/*',
  '/dashboard/stock-consultant/:id',
  '/dashboard/inventory-count/:id',
  '/dashboard/inventory-count/:id/sessions/:sessionId/reconciliation',
  '/dashboard/import/:id',
  '/dashboard/reports/:id',
  '/dashboard/settings/:id',
  '/dashboard/manage-users/:id',
  '/dashboard/companies/:id',
];

function useCompanyIdFromUrl(): number | null {
  const location = useLocation();
  for (const pattern of COMPANY_ID_ROUTE_PATTERNS) {
    const match = matchPath(pattern, location.pathname);
    const rawId = match?.params.id;
    if (rawId) {
      const parsed = Number(rawId);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

let messageIdCounter = 0;
function nextMessageId(): string {
  messageIdCounter += 1;
  return `ai-chat-msg-${Date.now()}-${messageIdCounter}`;
}

// Ultimos 5 intercambios exitosos (pregunta+respuesta) -- ver plan del
// modulo. Se excluyen mensajes con error/degradados para no confundir al
// LLM con "conversacion previa" que en realidad fueron rechazos.
function buildHistory(messages: AiChatMessageData[]): AiChatHistoryItem[] {
  const history: AiChatHistoryItem[] = [];
  for (let i = 0; i < messages.length - 1; i += 1) {
    const current = messages[i];
    const next = messages[i + 1];
    if (current.role === 'user' && next.role === 'assistant' && !next.isError && !next.isPending) {
      history.push({ question: current.text, answer: next.text });
    }
  }
  return history.slice(-5);
}

export default function AiChatWidget() {
  const { companies } = useMeCore();
  const urlCompanyId = useCompanyIdFromUrl();

  const [open, setOpen] = useState(false);
  const [manualCompanyId, setManualCompanyId] = useState<number | null>(null);
  const [messages, setMessages] = useState<AiChatMessageData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Solo empresas con el chatbot habilitado -- si esta apagado para una
  // empresa, esa empresa ni aparece como opcion (ni en el selector interno
  // ni como candidata implicita por :id de la URL, ver mas abajo).
  const companyOptions = useMemo(
    () =>
      companies
        .filter((company) => company.ai_chat_enabled)
        .map((company) => ({ id: Number(company.id), name: company.name })),
    [companies]
  );

  // Si la empresa de la URL actual tiene el chatbot apagado, se ignora como
  // candidata -- no fuerza esa empresa igual, cae al resto de la logica
  // (unica empresa habilitada, o selector si hay mas de una).
  const effectiveUrlCompanyId = useMemo(() => {
    if (urlCompanyId == null) return null;
    return companyOptions.some((c) => c.id === urlCompanyId) ? urlCompanyId : null;
  }, [urlCompanyId, companyOptions]);

  const selectedCompanyId = useMemo(() => {
    if (effectiveUrlCompanyId != null) return effectiveUrlCompanyId;
    if (manualCompanyId != null) return manualCompanyId;
    if (companyOptions.length === 1) return companyOptions[0].id;
    return null;
  }, [effectiveUrlCompanyId, manualCompanyId, companyOptions]);

  // Solo se pide elegir cuando de verdad hace falta: no hay :id valido en la
  // URL, no eligio manualmente todavia, y tiene mas de una empresa con el
  // chatbot habilitado.
  const showCompanyPicker = effectiveUrlCompanyId == null && selectedCompanyId == null && companyOptions.length > 1;

  const handleSend = useCallback(async () => {
    const question = inputValue.trim();
    if (!question || selectedCompanyId == null || isSending) return;

    const history = buildHistory(messages);
    const userMessage: AiChatMessageData = { id: nextMessageId(), role: 'user', text: question };
    const pendingId = nextMessageId();
    const pendingMessage: AiChatMessageData = { id: pendingId, role: 'assistant', text: '', isPending: true };

    setMessages((prev) => [...prev, userMessage, pendingMessage]);
    setInputValue('');
    setIsSending(true);

    try {
      const response = await aiChatService.query(selectedCompanyId, question, history);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === pendingId
            ? {
                id: pendingId,
                role: 'assistant',
                text: response.answer,
                columns: response.columns,
                rows: response.rows,
                sql: response.sql,
                chart: response.chart,
              }
            : m
        )
      );
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const text =
        typeof detail === 'string'
          ? detail
          : 'Ocurrió un error de conexión. Intenta de nuevo en unos segundos.';
      setMessages((prev) =>
        prev.map((m) => (m.id === pendingId ? { id: pendingId, role: 'assistant', text, isError: true } : m))
      );
    } finally {
      setIsSending(false);
    }
  }, [inputValue, selectedCompanyId, isSending, messages]);

  // Ninguna empresa asociada tiene el chatbot habilitado -- ni la burbuja
  // se muestra (pedido de Seba: si el modulo esta apagado, la funcion no
  // debe aparecer, no solo degradarse a un mensaje de error).
  if (companyOptions.length === 0) {
    return null;
  }

  return (
    <>
      <AiChatBubble open={open} onToggle={() => setOpen((prev) => !prev)} />
      <AiChatPanel
        open={open}
        onClose={() => setOpen(false)}
        messages={messages}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSend={handleSend}
        isSending={isSending}
        companies={companyOptions}
        selectedCompanyId={selectedCompanyId}
        onSelectCompany={setManualCompanyId}
        showCompanyPicker={showCompanyPicker}
      />
    </>
  );
}
